import { NavLink, Outlet } from 'react-router-dom'
import { AccessBanner } from './AccessBanner'
import { useAuth } from './auth'
import { BrandMark } from './BrandMark'
import { stopImpersonation } from './impersonation'
import { isNavGroup, visibleNav } from './nav'
import { NavBadge } from './NavBadge'
import { NavMenu } from './NavMenu'
import { NotificationBell } from './NotificationBell'
import { ProfileMenu } from './ProfileMenu'
import { SmartStatusIndicator } from './SmartStatusIndicator'
import { useNotifications } from './useNotifications'

/** Rahmen wie die Alt-Kopfleiste (.wf-header): obere Zeile mit Marke + zwei ruhigen Signalen rechts
 *  (Verbindungs-Punkt, Avatar mit Anwesenheits-Ring — Stempeluhr und Theme leben im Profil-Menü),
 *  darunter die rollenbasierten Navigations-Tabs, dann der Inhaltsbereich. Im Support-Modus (Betreiber
 *  arbeitet als Werkstatt) liegt darüber ein unübersehbarer Banner mit Beenden-Knopf. */
export function AppShell() {
  const { user, impersonating, access } = useAuth()
  const { data: notif } = useNotifications()
  if (!user) return null // ProtectedRoute garantiert den Nutzer; defensiv.

  // Ungelesen-Zähler je Nav-Bereich. Konvention: area == Pfad-Segment des Reiters ('/zeiten' → 'zeiten').
  const byArea = notif?.unreadByArea ?? {}
  const areaCount = (to: string): number => byArea[to.replace(/^\//, '')] ?? 0

  return (
    <div className="wf-app">
      {impersonating && (
        <div className="wf-impersonate" role="alert">
          <span className="wf-impersonate__text">
            🛠️ <b>Support-Modus:</b> Du arbeitest als Werkstatt „{impersonating.name}“ — alle Änderungen sind echt und werden protokolliert.
          </span>
          <button type="button" className="wf-btn wf-btn--sm wf-impersonate__stop" onClick={stopImpersonation}>
            Support-Modus beenden
          </button>
        </div>
      )}
      {access && <AccessBanner access={access} user={user} />}
      <header className="wf-header">
        <div className="wf-header__top">
          <span className="wf-brand" aria-hidden="true">
            <BrandMark size={22} />
          </span>
          <div className="wf-brandname">Cevio</div>
          <div className="wf-headctrls">
            <SmartStatusIndicator />
            <NotificationBell />
            <ProfileMenu user={user} />
          </div>
        </div>
        <nav className="wf-tabs" aria-label="Hauptnavigation">
          {visibleNav(user, !!impersonating).map((entry) =>
            isNavGroup(entry) ? (
              <NavMenu
                key={entry.label}
                label={entry.label}
                items={entry.items}
                badge={entry.items.reduce((sum, item) => sum + areaCount(item.to), 0)}
              />
            ) : (
              <NavLink
                key={entry.to}
                to={entry.to}
                className={({ isActive }) => (isActive ? 'wf-tab wf-tab--active' : 'wf-tab')}
              >
                {entry.label}
                <NavBadge n={areaCount(entry.to)} />
              </NavLink>
            ),
          )}
        </nav>
      </header>
      <main className="wf-main">
        <Outlet />
      </main>
    </div>
  )
}
